import store from '@/core/store'
import i18n from '@/core/utils/I18n'
import { dateFilterPeriods } from '@/common/components/dashboard/Labels'
import moment from 'moment-timezone'
import RestApi from '@/core/services/RestApi'

const getEnumTranslations = list => {
  let translations = {}
  Object.keys(list).forEach(key => {
    translations[key] = i18n.t(list[key])
  })
  return translations
}

const getDateFilterRange = (key = null) => {
  const dateFilter = store.getters.userConfig.dateFilter
  let range = JSON.parse(
    window.sessionStorage.getItem(
      key || `config.${store.getters.userUid}.dateRange`
    )
  )
  if (!range) {
    let startDayDiff = 0

    if (dateFilterPeriods[dateFilter.period]) {
      if (dateFilterPeriods[dateFilter.period].value > 0) {
        // current day will be included in the range as the end day so filter period is reduced by 1
        startDayDiff = dateFilterPeriods[dateFilter.period].value - 1
      } else {
        // no need to reduce by 1 if period selected is today
        startDayDiff = dateFilterPeriods[dateFilter.period].value
      }
    }

    range = {
      start: moment()
        .subtract(startDayDiff, 'days')
        .startOf('day'),
      // end date should be the end of the current day
      end: moment().endOf('day')
    }
  }

  return {
    start: moment(range.start).toDate(),
    end: moment(range.end).toDate()
  }
}

const projectRoute = (route, projectId) => {
  if (!projectId) {
    projectId = store.getters.projectId
  }

  if (!route.query) {
    route.query = {}
  }

  route.query.project_id = projectId
  return route
}

const projectEndpoint = (endpoint, projectId) => {
  endpoint.url = projectUrl(endpoint.url, projectId)

  return endpoint
}

const projectUrl = (url, projectId) => {
  if (!projectId) {
    projectId = store.getters.projectId
  }
  if (url !== '') {
    url = `projects/${projectId}/${url}`
  } else {
    url = `projects/${projectId}`
  }

  return url
}

const baseUrl = url => {
  if (url == null) {
    url = ''
  }
  return `${window.BASE_URL}/${url}`
}

const projectTimezoneDate = time => {
  if (time > 1e12) {
    time = Math.floor(time / 1000)
  }
  let date = moment.unix(time)
  if (store.state.app.project) {
    return date.tz(store.state.app.project.timezone)
  } else {
    return date
  }
}

const projectTimezoneTimestamp = timestamp => {
  // By default, moment parses and displays in local time.
  // If a UTC timestamp is parsed without the .utc it will be assumed that it's the local browser timezone
  if (typeof timestamp === 'number' && timestamp <= 1e12) {
    timestamp = timestamp * 1000
  }
  let dateInUtc = moment.utc(timestamp)
  if (store.state.app.project) {
    return dateInUtc.tz(store.state.app.project.timezone)
  } else {
    return dateInUtc
  }
}

const isDateFormat = date => {
  return moment(date, 'YYYY-MM-DD', true).isValid()
}

const formatNumber = number => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const toBase64 = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () =>
      resolve(addParamToBase64DataUrl(reader.result, 'name', file.name))
    reader.onerror = error => reject(error)
  })

const addParamToBase64DataUrl = (url, key, value) => {
  return url.replace(
    ';base64',
    ';' + key + '=' + value.replace(/[^a-zA-Z0-9-_.]/g, '-') + ';base64'
  )
}
const uuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    // eslint-disable-next-line one-var
    var r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const isValidUrl = str => {
  try {
    /* eslint-disable no-new */
    new URL(str)
    return true
  } catch (err) {
    return false
  }
}

const isSafeHttpUrl = str => {
  const trimmed = String(str || '').trim()
  if (!trimmed) return false
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):/i)
  return schemeMatch ? /^https?$/i.test(schemeMatch[1]) : true
}

const querySearch = (url, params, cb) => {
  return RestApi(
    projectEndpoint({
      method: 'get',
      url: url,
      params: params
    })
  )
    .then(({ data }) => {
      const list = data.map(item => {
        return {
          value: item.name
        }
      })
      cb(list)
    })
    .catch(error => {
      window.vue.$message({
        message: error.message,
        type: 'error'
      })
    })
}

const customAlert = (title, text, confirm, icon, customClass) => {
  const h = window.vue.$createElement
  return window.vue.$alert(
    h('div', null, [
      h('icon', {
        class: { icon: true },
        props: { glyph: icon || 'notice', size: '30' }
      }),
      h('div', { class: { 'msg-title': true } }, title),
      h('div', { class: { 'msg-text': true } }, text)
    ]),
    '',
    {
      customClass: 'custom-confirm' + (customClass || ''),
      confirmButtonText: confirm,
      center: true
    }
  )
}

const customConfirm = (title, text, confirm, cancel, icon, customClass) => {
  const h = window.vue.$createElement
  return window.vue.$confirm(
    h('div', null, [
      h('icon', {
        class: { icon: true },
        props: { glyph: icon || 'notice', size: '30' }
      }),
      h('div', { class: { 'msg-title': true } }, title),
      h('div', { class: { 'msg-text': true } }, text)
    ]),
    '',
    {
      customClass: 'custom-confirm' + (customClass || ''),
      confirmButtonText: confirm,
      cancelButtonText: cancel,
      center: true
    }
  )
}

export {
  getEnumTranslations,
  getDateFilterRange,
  projectRoute,
  projectEndpoint,
  projectUrl,
  projectTimezoneDate,
  projectTimezoneTimestamp,
  isDateFormat,
  formatNumber,
  toBase64,
  uuid,
  baseUrl,
  isValidUrl,
  isSafeHttpUrl,
  querySearch,
  customConfirm,
  customAlert
}



// WEBPACK FOOTER //
// ./src/core/helpers/index.js